import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Award, TrendingUp, TrendingDown, Save, ChevronDown, ChevronRight, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v, d = 2) => v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d });
const money = (v) =>
  v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// 2026-09-23 (chat: "Sniper Setup ones... not matching with other setups")
// — mirrors journal_service._filter_downgraded on the backend. This used
// to be duplicated inline in TWO places (TradeCard's "Stayed paper" banner
// and JournalPage's visibleTrades filter), and both still had the OLD,
// broader check (any truthy live_skip_reason) even after the backend's
// leaderboard/rollup fix — so a Sniper trade whose live_skip_reason was
// just "live_trading_off" (an operational toggle state, not the strategy
// judging the signal unfit) kept showing the "Stayed paper — excluded
// from stats" banner and kept getting hidden from the default trade list,
// even though the leaderboard above it (backend-computed) now correctly
// counts it. Consolidated into one helper so this can't drift out of sync
// a third time — keep in sync with journal_service._filter_downgraded's
// OPERATIONAL reason list if that ever changes.
const OPERATIONAL_SKIP_REASONS = ["live_trading_off", "daily_live_cap_reached"];
const isTradeDowngraded = (t) =>
  Boolean(t.rr_gate_failed) ||
  (Boolean(t.live_skip_reason) && !OPERATIONAL_SKIP_REASONS.includes(t.live_skip_reason));

const RollupStrip = ({ rollup }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="journal-rollup">
    {[
      { label: "Best Day of Week", data: rollup?.by_dow?.[0], k: "name", suffix: "" },
      { label: "Best Entry Hour",  data: rollup?.by_hour?.reduce?.((a, b) => a?.pnl_pct >= b?.pnl_pct ? a : b, null), k: "hour", suffix: ":00" },
      { label: "Best Setup",       data: rollup?.by_family?.[0], k: "family", suffix: "" },
    ].map((s, i) => (
      <div key={i} className="border border-grid p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data flex items-center gap-2">
          <Award size={12} /> {s.label}
        </div>
        <div className="font-heading text-2xl font-semibold mt-2">
          {s.data ? `${s.data[s.k]}${s.suffix}` : "—"}
        </div>
        {s.data && (
          <div className="text-xs text-muted-2 mt-1 font-mono-data">
            {s.data.trades} trades ·{" "}
            <span style={{ color: s.data.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
              {s.data.pnl_pct >= 0 ? "+" : ""}{fmt(s.data.pnl_pct, 2)}%
            </span>
          </div>
        )}
      </div>
    ))}
  </div>
);

const SetupLeaderboardRow = ({ row, rank, testid }) => {
  const [expanded, setExpanded] = useState(false);
  const pctColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");
  const pnlColor = row.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)";
  return (
    <>
      <tr
        className="border-b border-grid/40 hover:bg-white/[0.02] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
        data-testid={testid}
      >
        <td className="px-3 py-3">
          <span className="inline-flex items-center gap-2">
            <span className="text-muted-3">
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </span>
            <span
              className="font-mono-data text-[10px] w-5 text-center"
              style={{ color: rank <= 2 ? "var(--bull)" : "var(--text-muted-2)" }}
            >
              #{rank}
            </span>
            <span className="text-muted-1 font-semibold">{row.family}</span>
          </span>
        </td>
        <td className="px-3 py-3 text-right text-muted-2">{row.trades}</td>
        <td className="px-3 py-3 text-right text-muted-2">{fmt(row.win_rate_pct, 0)}%</td>
        <td className="px-3 py-3 text-right text-muted-2">{fmt(row.profit_factor, 1)}</td>
        <td className="px-3 py-3 text-right font-semibold" style={{ color: pnlColor }}>
          {row.pnl_pct >= 0 ? "+" : ""}{fmt(row.pnl_pct, 0)}%
        </td>
        <td className="px-3 py-3 text-right font-semibold"
            style={{ color: row.total_net_rupees == null ? "var(--text-muted-2)" : row.total_net_rupees >= 0 ? "var(--bull)" : "var(--bear)" }}>
          {money(row.total_net_rupees)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-grid/40" style={{ background: "rgba(255,255,255,0.015)" }}>
          <td colSpan={6} className="px-3 py-2.5">
            <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono-data text-[11px] text-muted-2 pl-9">
              <span>Wins <span style={{ color: "var(--bull)" }}>{row.wins}</span></span>
              <span>Losses <span style={{ color: "var(--bear)" }}>{row.losses}</span></span>
              <span>
                Avg Win{" "}
                <span style={{ color: pctColor(row.avg_win_pct) }}>
                  {row.avg_win_pct == null ? "—" : `+${fmt(row.avg_win_pct, 2)}%`}
                </span>
              </span>
              <span>
                Avg Loss{" "}
                <span style={{ color: pctColor(row.avg_loss_pct) }}>
                  {row.avg_loss_pct == null ? "—" : `${fmt(row.avg_loss_pct, 2)}%`}
                </span>
              </span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const SetupStatsTable = ({ byFamily }) => {
  if (!byFamily || byFamily.length === 0) return null;
  // by_family already arrives sorted desc by pnl_pct from the backend.
  return (
    <div className="border border-grid overflow-x-auto" data-testid="journal-setup-stats">
      <div className="px-3 pt-3 pb-1 font-heading text-sm font-semibold flex items-center gap-2">
        <Award size={14} /> Setup Leaderboard
      </div>
      <table className="w-full text-xs font-mono-data">
        <thead>
          <tr className="border-b border-grid text-muted-3 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2">Setup</th>
            <th className="text-right px-3 py-2">Trades</th>
            <th className="text-right px-3 py-2">Win %</th>
            <th className="text-right px-3 py-2">Profit Factor</th>
            <th className="text-right px-3 py-2">Net P&amp;L</th>
            <th className="text-right px-3 py-2" title="Actual post-brokerage rupee P&L (trade_economics) — blank for setups not yet wired into it">Net ₹</th>
          </tr>
        </thead>
        <tbody>
          {byFamily.map((row, i) => (
            <SetupLeaderboardRow
              key={row.family}
              row={row}
              rank={i + 1}
              testid={`journal-setup-stats-${row.family}`}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EXPIRY_PHASE_DOT = {
  EARLY: "#3ecf8e", MIDDLE: "#e8c547", LATE: "#e8834a", EXPIRY_WEEK: "#e8546a",
};

const ExpiryPhaseTable = ({ data }) => {
  // 2026-08-24 — per-section breakdown (chat: "needed in detail by section
  // wise so that I can identify which section is performing") — same
  // setup-selector pattern HourBreakdownTable already uses just below, so
  // the "All Setups" aggregate stays the default view but any one setup's
  // own expiry-phase money curve is one click away.
  const [setup, setSetup] = useState("all");
  const bySetup = data?.by_setup || {};
  const families = Object.keys(bySetup).sort();
  const phases = setup === "all" ? (data?.phases || []) : (bySetup[setup]?.phases || []);
  if ((data?.phases || []).length === 0) return null;
  const money = (v) =>
    v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  return (
    <div className="border border-grid overflow-x-auto" data-testid="journal-expiry-phase-table">
      <div className="px-3 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-sm font-semibold flex items-center gap-2">
          <Clock size={14} /> Money by Expiry Phase
        </div>
        <select
          value={setup}
          onChange={(e) => setSetup(e.target.value)}
          data-testid="journal-expiry-phase-setup-select"
          className="border border-grid bg-transparent px-2 py-1 text-[11px] font-mono-data text-muted-1"
        >
          <option value="all">All Setups</option>
          {families.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>
      <p className="px-3 pb-2 text-[11px] text-muted-3 max-w-2xl">
        Same % target, different days-to-expiry — does it actually pay less money late in the cycle?
        Net of estimated Upstox charges. Only setups instrumented for this collect real money data;
        {data?.unclassified_trades > 0 && (
          <> {data.unclassified_trades} trade{data.unclassified_trades === 1 ? "" : "s"} in this window aren't classified yet.</>
        )}
      </p>
      {phases.length === 0 ? (
        <p className="px-3 pb-3 text-[11px] text-muted-3">No classified trades for this setup in the selected window.</p>
      ) : (
      <table className="w-full text-xs font-mono-data">
        <thead>
          <tr className="border-b border-grid text-muted-3 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2">Expiry Phase</th>
            <th className="text-right px-3 py-2">Trades</th>
            <th className="text-right px-3 py-2">Win %</th>
            <th className="text-right px-3 py-2">Avg P&amp;L %</th>
            <th className="text-right px-3 py-2">Avg Net ₹</th>
            <th className="text-right px-3 py-2">Total Net ₹</th>
          </tr>
        </thead>
        <tbody>
          {phases.map((row) => (
            <tr key={row.expiry_phase} className="border-b border-grid/40 hover:bg-white/[0.02]"
                data-testid={`journal-expiry-phase-${row.expiry_phase}`}>
              <td className="px-3 py-2.5">
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full"
                        style={{ background: EXPIRY_PHASE_DOT[row.expiry_phase] || "var(--text-muted-2)" }} />
                  <span className="text-muted-1">{row.label}</span>
                </span>
              </td>
              <td className="px-3 py-2.5 text-right text-muted-2">{row.trades}</td>
              <td className="px-3 py-2.5 text-right text-muted-2">{fmt(row.win_rate_pct, 0)}%</td>
              <td className="px-3 py-2.5 text-right"
                  style={{ color: row.avg_pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                {row.avg_pnl_pct >= 0 ? "+" : ""}{fmt(row.avg_pnl_pct, 1)}%
              </td>
              <td className="px-3 py-2.5 text-right"
                  style={{ color: row.avg_net_rupees == null ? "var(--text-muted-2)" : row.avg_net_rupees >= 0 ? "var(--bull)" : "var(--bear)" }}>
                {money(row.avg_net_rupees)}
              </td>
              <td className="px-3 py-2.5 text-right font-semibold"
                  style={{ color: row.total_net_rupees == null ? "var(--text-muted-2)" : row.total_net_rupees >= 0 ? "var(--bull)" : "var(--bear)" }}>
                {money(row.total_net_rupees)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
    </div>
  );
};

const fmtHour = (h) => {
  const hr = ((h % 12) || 12);
  return `${hr} ${h < 12 ? "AM" : "PM"}`;
};

const HourBreakdownTable = ({ rollup }) => {
  const [setup, setSetup] = useState("all");
  const families = (rollup?.by_family || []).map((f) => f.family);
  const rows = setup === "all"
    ? (rollup?.by_hour || [])
    : (rollup?.hour_by_setup?.[setup] || []);
  if (!rollup || (rollup.by_hour || []).length === 0) return null;
  return (
    <div className="border border-grid overflow-x-auto" data-testid="journal-hour-breakdown">
      <div className="px-3 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-sm font-semibold">Hour Breakdown</div>
        <select
          value={setup}
          onChange={(e) => setSetup(e.target.value)}
          data-testid="journal-hour-setup-select"
          className="border border-grid bg-transparent px-2 py-1 text-[11px] font-mono-data text-muted-1"
        >
          <option value="all">All Setups</option>
          {families.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>
      <table className="w-full text-xs font-mono-data">
        <thead>
          <tr className="border-b border-grid text-muted-3 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2">Hour</th>
            <th className="text-right px-3 py-2">Trades</th>
            <th className="text-right px-3 py-2">Win %</th>
            <th className="text-right px-3 py-2">P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-4 text-center text-muted-3">
                No trades for this setup in the selected window.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.hour} className="border-b border-grid/40" data-testid={`journal-hour-row-${row.hour}`}>
                <td className="px-3 py-2.5 text-muted-1">{fmtHour(row.hour)}</td>
                <td className="px-3 py-2.5 text-right text-muted-2">{row.trades}</td>
                <td className="px-3 py-2.5 text-right text-muted-2">{fmt(row.win_rate_pct, 0)}%</td>
                <td className="px-3 py-2.5 text-right font-semibold"
                    style={{ color: row.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                  {row.pnl_pct >= 0 ? "+" : ""}{fmt(row.pnl_pct, 0)}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// 2026-09-06 — Nifty mood color coding, shared between the Mood Breakdown
// table and the Cautions panel below. Matches the 4-state classifier in
// market_mood.py (BULLISH/BEARISH/SIDEWAYS/SLUGGISH).
const MOOD_COLORS = {
  BULLISH: "var(--bull)",
  BEARISH: "var(--bear)",
  SIDEWAYS: "var(--text-muted-2)",
  SLUGGISH: "var(--warn, #d97706)",
};

const MoodBreakdownTable = ({ rollup }) => {
  const [setup, setSetup] = useState("all");
  const families = (rollup?.by_family || []).map((f) => f.family);
  // "All Setups" mood view isn't pre-aggregated on the backend (only
  // per-setup mood maps exist) — combine them client-side for the default
  // view rather than adding a redundant aggregate to the API response.
  const allMoodRows = React.useMemo(() => {
    const acc = {};
    for (const rows of Object.values(rollup?.mood_by_setup || {})) {
      for (const r of rows) {
        acc[r.mood] = acc[r.mood] || { mood: r.mood, trades: 0, wins: 0, pnl_pct: 0 };
        acc[r.mood].trades += r.trades;
        acc[r.mood].wins += r.wins;
        acc[r.mood].pnl_pct += r.pnl_pct;
      }
    }
    return Object.values(acc)
      .map((r) => ({ ...r, win_rate_pct: r.trades ? Math.round((r.wins / r.trades) * 1000) / 10 : 0 }))
      .sort((a, b) => b.trades - a.trades);
  }, [rollup]);
  const rows = setup === "all" ? allMoodRows : (rollup?.mood_by_setup?.[setup] || []);
  if (!rollup || Object.keys(rollup.mood_by_setup || {}).length === 0) return null;
  return (
    <div className="border border-grid overflow-x-auto" data-testid="journal-mood-breakdown">
      <div className="px-3 pt-3 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="font-heading text-sm font-semibold" title="Nifty mood at entry: BULLISH/BEARISH (trending), SIDEWAYS (choppy near VWAP), SLUGGISH (low range + volume regardless of direction)">
          By Nifty Mood
        </div>
        <select
          value={setup}
          onChange={(e) => setSetup(e.target.value)}
          data-testid="journal-mood-setup-select"
          className="border border-grid bg-transparent px-2 py-1 text-[11px] font-mono-data text-muted-1"
        >
          <option value="all">All Setups</option>
          {families.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
      </div>
      <table className="w-full text-xs font-mono-data">
        <thead>
          <tr className="border-b border-grid text-muted-3 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2">Mood</th>
            <th className="text-right px-3 py-2">Trades</th>
            <th className="text-right px-3 py-2">Win %</th>
            <th className="text-right px-3 py-2">P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-3 py-4 text-center text-muted-3">
                No mood data for this setup in the selected window.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.mood} className="border-b border-grid/40" data-testid={`journal-mood-row-${row.mood}`}>
                <td className="px-3 py-2.5 font-semibold" style={{ color: MOOD_COLORS[row.mood] || "var(--text-muted-1)" }}>
                  {row.mood}
                </td>
                <td className="px-3 py-2.5 text-right text-muted-2">{row.trades}</td>
                <td className="px-3 py-2.5 text-right text-muted-2">{fmt(row.win_rate_pct, 0)}%</td>
                <td className="px-3 py-2.5 text-right font-semibold"
                    style={{ color: row.pnl_pct >= 0 ? "var(--bull)" : "var(--bear)" }}>
                  {row.pnl_pct >= 0 ? "+" : ""}{fmt(row.pnl_pct, 0)}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// 2026-09-06 — "which setup should we avoid under specific conditions"
// (question 5 of the user's 5-question Journal ask). Straight read of
// rollup.cautions — backend already filtered to hour/mood buckets with a
// negative aggregate P&L on >=3 trades, sorted worst-first.
const CautionsPanel = ({ cautions }) => {
  if (!cautions || cautions.length === 0) return null;
  return (
    <div className="border border-grid overflow-hidden" data-testid="journal-cautions" style={{ borderColor: "var(--warn, #d97706)" }}>
      <div className="px-3 pt-3 pb-2 font-heading text-sm font-semibold flex items-center gap-2"
           style={{ color: "var(--warn, #d97706)" }}>
        <Clock size={14} /> Setups to Watch
      </div>
      <div className="px-3 pb-3 flex flex-col gap-1.5">
        {cautions.map((c, i) => (
          <div key={i} className="text-xs font-mono-data text-muted-1" data-testid={`journal-caution-${i}`}>
            <span style={{ color: "var(--bear)" }}>{fmt(c.pnl_pct, 0)}%</span>{" "}
            <span className="text-muted-2">— {c.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TradeCard = ({ trade, onSaveNote }) => {
  const [note, setNote] = useState(trade.user_note || "");
  const [saving, setSaving] = useState(false);
  const isLong = (trade.direction || "").toUpperCase() === "LONG";
  const dirColor = isLong ? "var(--bull)" : "var(--bear)";
  const pnl = Number(trade.pnl_pct || 0);
  const pnlColor = pnl >= 0 ? "var(--bull)" : "var(--bear)";
  const Icon = isLong ? TrendingUp : TrendingDown;
  // 2026-08-29 — chat: "Hope No of lots per order is also implemented for
  // Journal also. And considered while calculating Net Profit. Also
  // display no of lots." The backend side was already correct — every
  // setup's close-time trade_economics.compute_trade_economics() call
  // multiplies by (atm_lot_size * lots_placed/lots_per_trade), so net_pnl
  // already reflects the actual lot count. The gap was purely display:
  // this card only ever showed pnl_pct, never the lot count or the rupee
  // net P&L those lots produced. `lots_placed` (Candle Breakout/OI
  // Autopilot/Sniper) and `lots_per_trade` (VWAP Bounce) are the two field
  // names in use across setups; `net_pnl` is absent for setups
  // trade_economics hasn't been wired into (VWAP+OI, ORB, Gap-and-Go), so
  // both render conditionally rather than showing misleading zeros.
  const lots = trade.lots_placed ?? trade.lots_per_trade ?? null;
  const netPnl = trade.net_pnl;
  const hasNetPnl = netPnl != null;
  // 2026-08-31 — chat: "Journal page - new issues popped up" — the card
  // grid (list_trades, unfiltered) and every stat panel above it (rollup()/
  // expiry_phase_rollup(), both apply _filter_downgraded) draw from
  // deliberately different trade sets: trades the strategy's OWN pre-order
  // logic downgraded to paper (R:R floor / red-candle / VWAP-entry-filter —
  // rr_gate_failed or live_skip_reason set, see journal_service.py's
  // _filter_downgraded docstring) are shown as cards here but excluded from
  // every count/leaderboard/hour-breakdown/expiry-phase number above. That's
  // intentional (task: "Exclude R:R-gate-failed paper trades from Journal
  // win/loss stats"), but nothing on the card itself said so — so "N trades"
  // at the top never matching the number of cards on screen looked like
  // broken math instead of the documented filter it actually is. Same
  // "STAYED PAPER" label ExecutionJourney.jsx already uses for this.
  const downgraded = isTradeDowngraded(trade);
  const saveNote = async () => {
    setSaving(true);
    try { await onSaveNote(trade.trade_key, note); toast.success("Note saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="border border-grid surface flex flex-col"
         data-testid={`journal-trade-${trade.trade_key}`}>
      <div className="px-4 py-3 border-b border-grid flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: dirColor }} />
          <span className="font-heading text-base font-semibold">{trade.symbol}</span>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border font-mono-data"
                style={{ color: dirColor, borderColor: dirColor }}>
            {trade.setup_family}
            {trade.setup_family === "VWAP Bounce" && trade.setup_type
              ? ` · ${trade.setup_type}` : ""}
          </span>
        </div>
        <div className="font-mono-data text-sm font-bold" style={{ color: pnlColor }}>
          {pnl >= 0 ? "+" : ""}{fmt(pnl, 2)}%
        </div>
      </div>
      {downgraded && (
        <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-mono-data border-b"
             style={{ color: "#FFB66E", borderColor: "var(--border-grid)", background: "rgba(255,182,110,0.06)" }}
             title={trade.live_skip_reason || "R:R gate failed"}>
          Stayed paper — excluded from stats above
        </div>
      )}
      <div className="px-4 py-3 grid grid-cols-2 gap-2 font-mono-data text-[11px] text-muted-2">
        <div>Entry ₹{fmt(trade.entry_price ?? trade.entry_spot)}</div>
        <div>Exit ₹{fmt(trade.exit_spot ?? trade.exit_price)}</div>
        {(lots || hasNetPnl) && (
          <div className="col-span-2 flex items-center justify-between">
            <span>{lots ? `${lots} lot${Number(lots) === 1 ? "" : "s"}` : ""}</span>
            {hasNetPnl && (
              <span className="font-semibold" style={{ color: netPnl >= 0 ? "var(--bull)" : "var(--bear)" }}>
                Net {netPnl >= 0 ? "+" : "-"}₹{Math.abs(netPnl).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </span>
            )}
          </div>
        )}
        <div className="col-span-2">
          {trade.day} · {trade.entry_ist?.slice?.(11, 16) || "—"}
          {" → "}{trade.exit_ist?.slice?.(11, 16) || (trade.close_reason || "OPEN")}
        </div>
      </div>
      <div className="px-4 pb-3">
        <textarea rows={2} placeholder="Post-mortem note (what went right/wrong)…"
          value={note} onChange={(e) => setNote(e.target.value)}
          className="w-full border border-grid bg-transparent p-2 font-mono-data text-[11px] text-muted-2"
          data-testid={`journal-note-${trade.trade_key}`} />
        <button onClick={saveNote} disabled={saving}
          className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 border border-grid text-[10px] uppercase font-mono-data hover:bg-white/5"
          data-testid={`journal-save-${trade.trade_key}`}>
          <Save size={10} /> {saving ? "Saving…" : "Save note"}
        </button>
      </div>
    </div>
  );
};

// 2026-10-13 — LOG-ONLY late-entry flag (backend market_context.py): trades
// entered after Nifty had already moved >= 0.2% (vs previous close) in the
// trade's own direction. Aug-Sep backtest: those were PF 0.41 vs 1.11 for the
// rest. Replaces the Nifty mood label as the market-context check to watch.
const LateEntryCard = ({ days }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/market-context/summary`, { params: { days: Math.max(days || 0, 1) } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [days]);
  if (!d) return null;
  const f = (v, s = "") => (v == null ? "—" : `${v}${s}`);
  const Row = ({ label, st, strong }) => (
    <tr className="border-b border-grid/40">
      <td className={`px-3 py-1.5 ${strong ? "" : "text-muted-2"}`}>{label}</td>
      <td className="px-3 py-1.5">{f(st?.trades)}</td>
      <td className="px-3 py-1.5">{f(st?.win_rate_pct, "%")}</td>
      <td className="px-3 py-1.5" style={{ color: (st?.avg_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
        {st?.avg_pnl_pct == null ? "—" : `${st.avg_pnl_pct >= 0 ? "+" : ""}${st.avg_pnl_pct}%`}
      </td>
      <td className="px-3 py-1.5">{f(st?.profit_factor)}</td>
    </tr>
  );
  const pair = (label, x) => (
    <React.Fragment key={label}>
      <Row label={`${label} · late`} st={x?.late} />
      <Row label={`${label} · other`} st={x?.other} />
    </React.Fragment>
  );
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-late-entry-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
        Late entry (log only) · Nifty already ≥{d.threshold_pct}% in the trade's direction at entry
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        CE bought with Nifty already up ≥{d.threshold_pct}%, or PE bought with Nifty already down ≥{d.threshold_pct}%.
        Nothing is blocked. Aug–Sep backtest: late trades PF 0.41 vs 1.11 for the rest.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["", "Trades", "Win rate", "Avg", "PF"].map((h) => <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          <Row label="Late entries" st={d.late} strong />
          <Row label="All other" st={d.other} strong />
          {pair("CE", d.by_side?.CE)}
          {pair("PE", d.by_side?.PE)}
          {Object.entries(d.by_setup || {}).map(([k, v]) => pair(k, v))}
        </tbody>
      </table>
      {(d.trades || []).length > 0 && (
        <>
          <button className="text-[10px] underline text-muted-2 font-mono-data" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} trades
          </button>
          {open && (
            <table className="w-full text-[11px] font-mono-data">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  {["Day", "Setup", "Symbol", "Side", "Nifty my way", "VIX chg", "Late", "P&L"].map((h) => (
                    <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.trades.map((r, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-3 py-1.5">{r.day}</td>
                    <td className="px-3 py-1.5 text-muted-2">{r.setup}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.symbol}</td>
                    <td className="px-3 py-1.5">{r.side}</td>
                    <td className="px-3 py-1.5">{f(r.nifty_move_dir_pct, "%")}</td>
                    <td className="px-3 py-1.5">{f(r.vix_chg_pct, "%")}</td>
                    <td className="px-3 py-1.5" style={{ color: r.late ? "var(--bear)" : undefined }}>{r.late ? "LATE" : "—"}</td>
                    <td className="px-3 py-1.5" style={{ color: (r.pnl ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                      {r.pnl == null ? "—" : `${r.pnl >= 0 ? "+" : ""}${fmt(r.pnl, 2)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

// 2026-10-17 — LOG-ONLY market regime (backend market_regime.py): % of F&O stocks
// above their 20-day EMA at the previous close (BEARISH <35%, MIXED, BULLISH >65%),
// intraday advances/declines and India VIX — CE vs PE results under each, to decide
// later whether Sniper (or others) should favour one side by regime.
const RegimeCard = ({ days }) => {
  const [d, setD] = useState(null);
  const [today, setToday] = useState(null);
  const [setup, setSetup] = useState("All");
  useEffect(() => {
    axios.get(`${API}/market-context/summary`, { params: { days: Math.max(days || 0, 1) } })
      .then((r) => setD(r.data)).catch(() => setD(null));
    axios.get(`${API}/market-regime/today`).then((r) => setToday(r.data)).catch(() => setToday(null));
  }, [days]);
  if (!d) return null;
  const f = (v, s = "") => (v == null ? "—" : `${v}${s}`);
  const cell = (st) => (
    <td className="px-3 py-1.5">
      {st?.trades ? (
        <span>
          {st.trades} · {f(st.win_rate_pct, "%")} ·{" "}
          <span style={{ color: (st.profit_factor ?? 0) >= 1 ? "var(--bull)" : "var(--bear)" }}>PF {f(st.profit_factor)}</span>
        </span>
      ) : "—"}
    </td>
  );
  const section = (title, obj) => (
    <React.Fragment key={title}>
      <tr><td colSpan={3} className="px-3 pt-3 pb-1 text-[9px] uppercase tracking-[0.15em] text-muted-3">{title}</td></tr>
      {Object.keys(obj || {}).length === 0 ? (
        <tr><td colSpan={3} className="px-3 py-1.5 text-muted-3">no tagged trades yet</td></tr>
      ) : Object.entries(obj).map(([k, v]) => (
        <tr key={k} className="border-b border-grid/40">
          <td className="px-3 py-1.5 text-muted-2">{k}</td>
          {cell(v?.CE)}
          {cell(v?.PE)}
        </tr>
      ))}
    </React.Fragment>
  );
  const setups = ["All", ...Object.keys(d.by_setup_regime || {})];
  const b = today?.breadth || {};
  const last = today?.last_snap;
  const regimeColor = b.regime === "BEARISH" ? "var(--bear)" : b.regime === "BULLISH" ? "var(--bull)" : undefined;
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-regime-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Market regime &amp; breadth (log only) · CE vs PE
        </div>
        <select value={setup} onChange={(e) => setSetup(e.target.value)}
                className="bg-transparent border border-grid text-[10px] font-mono-data px-2 py-1">
          {setups.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {b.regime && (
        <div className="text-[11px] font-mono-data">
          Today: <span style={{ color: regimeColor, fontWeight: 600 }}>{b.regime}</span>
          {" "}· {f(b.pct_above_ema20, "%")} of F&amp;O stocks above 20-day EMA · {f(b.pct_above_sma50, "%")} above 50-day SMA
          {" "}· Nifty {today?.nifty?.above_ema20 ? "above" : "below"} its EMA20
          {" "}· VIX {f(today?.vix?.prev_close)} (20-day avg {f(today?.vix?.avg_20d)})
          {last && <> · {last.t}: {last.adv} up / {last.dec} down, VIX {f(last.vix_chg_pct, "%")}</>}
        </div>
      )}
      <div className="text-[10px] text-muted-3 font-mono-data">
        Regime = % of F&amp;O stocks above their 20-day EMA at the previous close: BEARISH &lt;35%, MIXED, BULLISH &gt;65%.
        Nothing is blocked. Aug–Oct Sniper: PE PF 5.6 on bearish days vs 0.5 on mixed; CE PF 1.1 vs 1.5.
        {" "}{d.regime_tagged} of the closed trades in this window carry a regime tag.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["", "CE · trades · win · PF", "PE · trades · win · PF"].map((h) => <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {setup === "All" ? (
            <>
              {section("Daily regime", d.by_regime)}
              {section("Intraday breadth at entry (% of F&O stocks up)", d.by_breadth)}
              {section("India VIX change at entry", d.by_vix)}
              {section("RSI divergence at entry (3-min, log only)", d.by_rsi_div)}
              {section("Target vs stop at entry", d.by_rr)}
            </>
          ) : (
            <>
              {section(`Daily regime · ${setup}`, (d.by_setup_regime || {})[setup])}
              {/* 2026-10-30 — per-setup breadth / late entry / target-vs-stop (CB review) */}
              {section(`Intraday breadth at entry · ${setup}`, (d.by_setup_breadth || {})[setup])}
              {section(`Late entry (Nifty already ≥0.2% in the trade's direction) · ${setup}`, (d.by_setup_late || {})[setup])}
              {section(`Target vs stop at entry · ${setup}`, (d.by_setup_rr || {})[setup])}
              {section(`RSI divergence · ${setup}`, (d.by_setup_rsi_div || {})[setup])}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

// 2026-10-30 — LOG ONLY (backend entry_odds.py): "target-first odds at entry" — the rest of
// the session replayed 3,000 times from the stock's own same-time-of-day 5-min bars (idea:
// day-trader-skill's Time Machine), vs what the Sniper / CB trades actually did.
const EntryOddsCard = ({ days }) => {
  const [d, setD] = useState(null);
  const [setup, setSetup] = useState("All");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/entry-odds/summary`, { params: { days: Math.max(days || 0, 1) } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [days]);
  if (!d) return null;
  const f = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);
  const src = setup === "All" ? d : (d.by_setup || {})[setup] || {};
  const buckets = src.by_bucket || [];
  const overall = setup === "All" ? d.overall : src.overall;
  const trades = (d.trades || []).filter((t) => setup === "All" || t.setup === setup);
  const pf = (x) => <span style={{ color: (x ?? 0) >= 1 ? "var(--bull)" : "var(--bear)" }}>{f(x)}</span>;
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-entry-odds-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Odds at entry (log only) · target first vs stop first
        </div>
        <select value={setup} onChange={(e) => setSetup(e.target.value)}
                className="bg-transparent border border-grid text-[10px] font-mono-data px-2 py-1">
          {["All", ...Object.keys(d.by_setup || {})].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        At each entry the rest of the day (to 15:15) is replayed {f(d.method?.replays)} times from the stock&apos;s own 5-min bars at the
        same time of day over its last {f(d.method?.pool_sessions)} sessions, with the option target/stop turned into stock moves
        (premium ÷ delta × price). &quot;Lean&quot; = target&apos;s share of the replays that hit a side minus the share needed to break even.
        Nothing is blocked. Worth a rule only if &quot;leans target&quot; clearly beats &quot;leans stop&quot; over 2–3 weeks.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["Odds at entry", "Trades", "Won", "Avg", "PF", "Avg p(target)"].map((h) => (
              <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {buckets.length === 0 ? (
            <tr><td colSpan={6} className="px-3 py-2 text-muted-3">No closed trades with odds yet.</td></tr>
          ) : buckets.map((b) => (
            <tr key={b.bucket} className="border-b border-grid/40">
              <td className="px-3 py-1.5 text-muted-2">{b.label}</td>
              <td className="px-3 py-1.5">{b.trades}</td>
              <td className="px-3 py-1.5">{f(b.win_rate_pct, "%")}</td>
              <td className="px-3 py-1.5">{f(b.avg_pnl_pct, "%")}</td>
              <td className="px-3 py-1.5">{pf(b.profit_factor)}</td>
              <td className="px-3 py-1.5 text-muted-2">{f(b.avg_p_target, "%")}</td>
            </tr>
          ))}
          {overall?.trades ? (
            <tr><td className="px-3 py-1.5 font-semibold">All</td><td className="px-3 py-1.5">{overall.trades}</td>
              <td className="px-3 py-1.5">{f(overall.win_rate_pct, "%")}</td><td className="px-3 py-1.5">{f(overall.avg_pnl_pct, "%")}</td>
              <td className="px-3 py-1.5">{pf(overall.profit_factor)}</td><td className="px-3 py-1.5 text-muted-2">{f(overall.avg_p_target, "%")}</td></tr>
          ) : null}
        </tbody>
      </table>
      {trades.length > 0 && (
        <button className="text-[10px] font-mono-data underline text-muted-3" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Show"} trades ({trades.length})
        </button>
      )}
      {open && (
        <table className="w-full text-[10px] font-mono-data">
          <thead className="text-muted-3 uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              {["Day", "Setup", "Symbol", "Side", "p(target)", "p(stop)", "Lean", "Past days T/S", "Result"].map((h) => (
                <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {trades.map((t, i) => (
              <tr key={`${t.symbol}-${t.day}-${i}`} className="border-b border-grid/40">
                <td className="px-2 py-1 text-muted-3">{t.day}</td>
                <td className="px-2 py-1 text-muted-2">{t.setup}</td>
                <td className="px-2 py-1">{t.symbol}{t.live ? " ·live" : ""}</td>
                <td className="px-2 py-1">{t.side}</td>
                <td className="px-2 py-1">{f(t.p_target != null ? Math.round(t.p_target * 100) : null, "%")}</td>
                <td className="px-2 py-1">{f(t.p_stop != null ? Math.round(t.p_stop * 100) : null, "%")}</td>
                <td className="px-2 py-1" style={{ color: (t.lean_pp ?? 0) >= 5 ? "var(--bull)" : (t.lean_pp ?? 0) <= -5 ? "var(--bear)" : undefined }}>
                  {t.lean_pp != null ? `${t.lean_pp > 0 ? "+" : ""}${t.lean_pp}` : "—"}
                </td>
                <td className="px-2 py-1 text-muted-2">{t.history ? `${t.history.target}/${t.history.stop}` : "—"}</td>
                <td className="px-2 py-1" style={{ color: (t.pnl ?? 0) > 0 ? "var(--bull)" : "var(--bear)" }}>
                  {t.pnl != null ? `${t.pnl > 0 ? "+" : ""}${t.pnl}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// 2026-11-01 — RESEARCH (backend stock_lab.py, Jobs → "Stock lab" → Run now): 1-year backtest
// of VWAP+OI / ORB / Gap-and-Go on Kite 5-min candles, priced as the ATM stock option, judged
// against a pass bar fixed before the run. Polls while a run is in progress.
const SETUP_LABEL = { sip_orb: "Stocks-in-Play ORB (paper)", candle_breakout: "Candle Breakout", sniper: "Sniper", vwap_oi: "VWAP + OI", orb: "ORB", gap_and_go: "Gap-and-Go",
  steady_trend: "Steady Trend", momentum_ign: "Momentum Ignition (no OI, approx.)" };
// 2026-11-01 — LOG ONLY (backend stock_option_ctx.py): the stock's own option chain at each CB /
// Sniper entry (IV vs realised vol, room to the OI wall, PCR, gamma) vs how the trade ended.
const StockOptionCtxCard = ({ days }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/stock-option-ctx/summary`, { params: { days: Math.max(days || 0, 60) } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [days]);
  if (!d) return null;
  const f = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-stock-option-ctx-card">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
        Stock option chain at entry (log only) · CB + Sniper · {d.trades} closed trades
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        One chain call per new trade. Buckets need ~60 trades (4-6 weeks) before they mean anything; nothing is blocked.
        IV / realised vol &gt; 1 = paying a rich premium; room to the OI wall = distance to the biggest call OI above (CE) / put OI below (PE).
      </div>
      {d.trades === 0 ? <div className="text-[11px] text-muted-3 font-mono-data">No closed trades with the chain logged yet.</div> : (
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">{["Field", "Bucket", "Trades", "Won", "Avg", "PF"].map((h) => <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {Object.entries(d.buckets || {}).flatMap(([field, bs]) => Object.entries(bs).map(([b, st]) => (
              <tr key={field + b} className="border-b border-grid/40">
                <td className="px-2 py-1 text-muted-2">{field}</td><td className="px-2 py-1">{b}</td><td className="px-2 py-1">{f(st.n)}</td>
                <td className="px-2 py-1">{f(st.won_pct, "%")}</td>
                <td className="px-2 py-1" style={{ color: (st.avg_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>{f(st.avg_pct, "%")}</td>
                <td className="px-2 py-1">{f(st.pf)}</td>
              </tr>)))}
          </tbody>
        </table>)}
    </div>
  );
};

// 2026-11-01 — CB / Sniper research (stock_lab.research_summary): exits and one entry filter
// CHOSEN on the first half of the window, CONFIRMED unchanged on the second half.
const StockLabResearch = ({ rs }) => {
  const [open, setOpen] = useState(null);
  const f = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);
  const col = (good) => ({ color: good ? "var(--bull)" : "var(--bear)" });
  const S = ({ s }) => (s && s.n
    ? <span>{s.n} · {f(s.win, "%")} won · avg <span style={col((s.avg ?? 0) > 0)}>{f(s.avg, "%")}</span> · PF <span style={col((s.pf ?? 0) >= 1.3)}>{f(s.pf)}</span></span>
    : <span className="text-muted-3">—</span>);
  return (
    <div className="mt-3 border-t border-grid pt-3 space-y-2" data-testid="stock-lab-research">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
        CB / Sniper research · exits + one entry filter chosen before {rs.split_day}, confirmed after it
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Research only — changes nothing live and no verdict above. The best exit (of {rs.exits?.length}) and the best single entry filter
        (keeping ≥ {Math.round((rs.min_keep_frac || 0.4) * 100)}% of trades) are picked on the first half; the second half then has to show
        ≥ {rs.confirm_bar?.min_test_trades} trades, PF ≥ {rs.confirm_bar?.min_test_pf}, positive average and still positive without its 5 best
        trades, at 3% friction.
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
          <tr className="border-b border-grid">
            {["Setup", "Today's exit (2nd half)", "Chosen exit", "Chosen filter", "1st half (chosen)", "2nd half (confirm)", "2nd half @1%", "Result"].map((h) => (
              <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {["candle_breakout", "sniper"].map((k) => {
            const x = rs[k];
            if (!x) return null;
            if (x.note) return <tr key={k}><td className="px-2 py-1.5">{SETUP_LABEL[k]}</td><td colSpan={7} className="px-2 text-muted-3">{x.note}</td></tr>;
            return (
              <React.Fragment key={k}>
                <tr className="border-b border-grid/40 cursor-pointer" onClick={() => setOpen(open === k ? null : k)}>
                  <td className="px-2 py-1.5 font-semibold">{SETUP_LABEL[k]}</td>
                  <td className="px-2 py-1.5 text-muted-2">{x.base?.exit}: <S s={x.base?.test} /></td>
                  <td className="px-2 py-1.5">{x.chosen_exit}</td>
                  <td className="px-2 py-1.5">{x.chosen_filter}</td>
                  <td className="px-2 py-1.5"><S s={x.chosen_train} /></td>
                  <td className="px-2 py-1.5"><S s={x.chosen_test} /></td>
                  <td className="px-2 py-1.5 text-muted-2"><S s={x.chosen_test_1pct} /></td>
                  <td className="px-2 py-1.5"><span style={col(x.confirmed)}>{x.confirmed ? "CONFIRMED" : "NOT CONFIRMED"}</span></td>
                </tr>
                {open === k && (
                  <tr><td colSpan={8} className="px-2 py-2 bg-black/20 space-y-2">
                    <div className="text-[10px] text-muted-3 font-mono-data">
                      {Object.entries(x.checks || {}).map(([c, ok]) => `${ok ? "✓" : "✗"} ${c}`).join(" · ")}
                    </div>
                    <table className="w-full text-[10px] font-mono-data">
                      <thead className="text-muted-3 uppercase tracking-[0.15em]">
                        <tr className="border-b border-grid">
                          {["Top exits (ranked on 1st half)", "1st half", "2nd half", "2nd half @1%"].map((h) => (
                            <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>))}
                        </tr>
                      </thead>
                      <tbody>
                        {(x.top_exits || []).map((g) => (
                          <tr key={g.exit} className="border-b border-grid/40">
                            <td className="px-2 py-1">{g.exit}</td><td className="px-2 py-1"><S s={g.train} /></td>
                            <td className="px-2 py-1"><S s={g.test} /></td><td className="px-2 py-1 text-muted-2"><S s={g.test_1pct} /></td>
                          </tr>))}
                      </tbody>
                    </table>
                    <table className="w-full text-[10px] font-mono-data">
                      <thead className="text-muted-3 uppercase tracking-[0.15em]">
                        <tr className="border-b border-grid">
                          {["Entry filter (with the chosen exit)", "Keeps", "1st half", "2nd half"].map((h) => (
                            <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.values(x.filters || {}).flatMap((rows) => rows.slice(0, 2)).map((rw) => (
                          <tr key={rw.rule} className="border-b border-grid/40">
                            <td className="px-2 py-1">{rw.rule}</td><td className="px-2 py-1 text-muted-2">{Math.round((rw.keep_frac || 0) * 100)}%</td>
                            <td className="px-2 py-1"><S s={rw.train} /></td><td className="px-2 py-1"><S s={rw.test} /></td>
                          </tr>))}
                      </tbody>
                    </table>
                  </td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const StockLabCard = () => {
  const [d, setD] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  useEffect(() => {
    let timer;
    const load = () => axios.get(`${API}/stock-lab/summary`).then((r) => {
      setD(r.data);
      if (r.data?.state?.status === "RUNNING") timer = setTimeout(load, 15000);
    }).catch(() => setD(null));
    load();
    return () => clearTimeout(timer);
  }, []);
  if (!d || (!d.run && !d.state?.status)) return null;
  const st = d.state || {};
  const run = d.run;
  const f = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);
  const col = (v, good) => ({ color: v == null ? undefined : good ? "var(--bull)" : "var(--bear)" });
  const rows = [];
  Object.entries(run?.results || {}).forEach(([setup, r]) => {
    Object.entries(r.variants || {}).forEach(([v, x]) => {
      rows.push({ key: `${setup}|${v}`, setup, v, x, primary: v === r.primary, pass: r.pass, pass1: r.pass_1pct,
                  riskUnit: r.unit === "risk" && !v.startsWith("ATM") });
    });
  });
  const Line = ({ label, s, h1, h2 }) => (
    <tr className="border-b border-grid/40">
      <td className="px-2 py-1 text-muted-2">{label}</td>
      <td className="px-2 py-1">{f(s?.trades)}</td>
      <td className="px-2 py-1">{f(s?.win_rate_pct, "%")}</td>
      <td className="px-2 py-1" style={col(s?.avg_pct, (s?.avg_pct ?? 0) > 0)}>{f(s?.avg_pct, "%")}</td>
      <td className="px-2 py-1" style={col(s?.pf, (s?.pf ?? 0) >= 1.3)}>{f(s?.pf)}</td>
      <td className="px-2 py-1" style={col(h1?.net_pct_sum, (h1?.net_pct_sum ?? 0) > 0)}>{f(h1?.pf)}</td>
      <td className="px-2 py-1" style={col(h2?.net_pct_sum, (h2?.net_pct_sum ?? 0) > 0)}>{f(h2?.pf)}</td>
      <td className="px-2 py-1" style={col(s?.net_ex_top5, (s?.net_ex_top5 ?? 0) > 0)}>{f(s?.net_ex_top5, "%")}</td>
      <td className="px-2 py-1 text-muted-2">{s?.avg_rs_lot != null ? `₹${s.avg_rs_lot}` : "—"}</td>
    </tr>
  );
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-stock-lab-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Stock lab (research) · stock setups · 1 year
        </div>
        <div className="text-[10px] font-mono-data text-muted-3">
          {st.status === "RUNNING" ? `Running… ${f(st.done)}/${f(st.total)} · ${st.message || ""}`
            : st.status === "ERROR" ? <span style={{ color: "var(--bear)" }}>{st.message}</span>
            : run ? `${run.from} → ${run.to} · ${run.symbols} stocks · ${run.sessions} sessions` : ""}
        </div>
      </div>
      {run && (
        <>
          <div className="text-[10px] text-muted-3 font-mono-data">
            Every signal, priced as the ATM stock option (delta {run.assumptions?.delta}, theta, {run.assumptions?.friction_pct_of_premium}%
            spread, ₹{run.assumptions?.charges_rs_per_lot}/lot). Returns are % of premium per trade. Pass bar (fixed before the run): ≥ 100 trades,
            PF ≥ 1.3, both halves positive (split {run.split_day}), still positive without the 5 best trades — for every signal AND the
            capped portfolio. Verdicts use {run.assumptions?.friction_pct_of_premium}% friction; &quot;PF @1%&quot; is a sensitivity check only.
            {run.assumptions?.window ? ` Window ${run.assumptions.window}.` : ""} Halves show PF, coloured by net.
            {run.assumptions?.known_biases?.length ? (
              <span title={run.assumptions.known_biases.join("\n")} className="underline decoration-dotted cursor-help">
                {" "}Known biases (all flatter the results): {run.assumptions.known_biases.length}
              </span>) : null}
          </div>
          <table className="w-full text-[11px] font-mono-data">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr className="border-b border-grid">
                {["Setup · variant", "Trades", "Won", "Avg", "PF", "PF @1%", "1st half", "2nd half", "Net w/o top 5", "Avg ₹/lot", "Verdict"].map((h) => (
                  <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const a = r.x.all || {};
                return (
                  <React.Fragment key={r.key}>
                    <tr className="border-b border-grid/40 cursor-pointer" onClick={() => setOpenKey(openKey === r.key ? null : r.key)}>
                      <td className="px-2 py-1.5">
                        <span className={r.primary ? "font-semibold" : "text-muted-2"}>{SETUP_LABEL[r.setup] || r.setup}</span>
                        <span className="text-muted-3"> · {r.v}{r.primary ? " (primary)" : ""}{r.riskUnit ? " · returns in % of ₹1k risk (100 = +1R), ₹ column = net per trade" : ""}</span>
                      </td>
                      <td className="px-2 py-1.5">{f(a.trades)}</td>
                      <td className="px-2 py-1.5">{f(a.win_rate_pct, "%")}</td>
                      <td className="px-2 py-1.5" style={col(a.avg_pct, (a.avg_pct ?? 0) > 0)}>{f(a.avg_pct, "%")}</td>
                      <td className="px-2 py-1.5" style={col(a.pf, (a.pf ?? 0) >= 1.3)}>{f(a.pf)}</td>
                      <td className="px-2 py-1.5 text-muted-2">{f(a.pf_1pct)}</td>
                      <td className="px-2 py-1.5" style={col(r.x.first_half?.net_pct_sum, (r.x.first_half?.net_pct_sum ?? 0) > 0)}>{f(r.x.first_half?.pf)}</td>
                      <td className="px-2 py-1.5" style={col(r.x.second_half?.net_pct_sum, (r.x.second_half?.net_pct_sum ?? 0) > 0)}>{f(r.x.second_half?.pf)}</td>
                      <td className="px-2 py-1.5" style={col(a.net_ex_top5, (a.net_ex_top5 ?? 0) > 0)}>{f(a.net_ex_top5, "%")}</td>
                      <td className="px-2 py-1.5 text-muted-2">{a.avg_rs_lot != null ? `₹${a.avg_rs_lot}` : "—"}</td>
                      <td className="px-2 py-1.5">
                        {r.primary
                          ? <span style={col(1, r.pass)}>{r.pass ? "PASS" : "FAIL"}
                              <span className="text-muted-3">{r.pass1 != null ? ` (1%: ${r.pass1 ? "pass" : "fail"})` : ""}</span></span>
                          : <span className="text-muted-3">{r.x.verdict?.pass ? "would pass" : "—"}</span>}
                      </td>
                    </tr>
                    {openKey === r.key && (
                      <tr><td colSpan={11} className="px-2 py-2 bg-black/20">
                        <div className="text-[10px] text-muted-3 font-mono-data mb-1">
                          Checks: {Object.entries(r.x.verdict?.checks || {}).map(([k, ok]) => `${ok ? "✓" : "✗"} ${k}`).join(" · ")}
                          {r.x.verdict?.capped_checks ? ` · capped: ${Object.entries(r.x.verdict.capped_checks).map(([k, ok]) => `${ok ? "✓" : "✗"} ${k}`).join(" · ")}` : ""}
                          {" · "}exits {Object.entries(a.exits || {}).map(([k, n]) => `${k} ${n}`).join(", ")}
                          {" · "}avg stock move {f(a.avg_stock_pct, "%")} · avg gross {f(a.avg_gross_pct, "%")}
                        </div>
                        <table className="w-full text-[10px] font-mono-data">
                          <thead className="text-muted-3 uppercase tracking-[0.15em]">
                            <tr className="border-b border-grid">
                              {["Slice", "Trades", "Won", "Avg", "PF", "1st half", "2nd half", "w/o top 5", "₹/lot"].map((h) => (
                                <th key={h} className="text-left px-2 py-1 font-normal">{h}</th>))}
                            </tr>
                          </thead>
                          <tbody>
                            <Line label={`Capped portfolio (as the scanner would take)`} s={r.x.capped} h1={r.x.capped_first_half} h2={r.x.capped_second_half} />
                            {Object.entries(r.x.by_quarter || {}).map(([q, s]) => <Line key={q} label={q} s={s} />)}
                            {Object.entries({ ...(r.x.by_side || {}), ...(r.x.by_window || {}), ...(r.x.by_gap || {}),
                              ...(r.x.by_scan || {}), ...(r.x.by_hour || {}), ...(r.x.by_live_filter || {}), ...(r.x.by_anticipated || {}), ...(r.x.by_rvol || {}) }).map(([k, s]) => <Line key={k} label={k} s={s} />)}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {run.missing_count > 0 && (
            <div className="text-[10px] text-muted-3 font-mono-data">No candles for {run.missing_count} symbols (skipped).</div>
          )}
          {run.research && <StockLabResearch rs={run.research} />}
        </>
      )}
    </div>
  );
};

// 2026-10-17 — LOG ONLY (backend setup_shadow.py, runs 15:47): what the picks each
// filter dropped would have done (Sniper single-filter near misses, Candle Breakout
// skips) and how real trades moved after entry (whipsaw = stopped, then hit target).
const SetupShadowCard = ({ days }) => {
  const [d, setD] = useState(null);
  const [setup, setSetup] = useState("Candle Breakout");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/setup-shadow/summary`, { params: { days: Math.max(days || 0, 1), setup } })
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [days, setup]);
  if (!d) return null;
  const s = (d.setups || {})[setup] || {};
  const f = (v, suf = "") => (v == null ? "—" : `${v}${suf}`);
  const st = (x) => (x?.trades
    ? <span>{x.trades} · {f(x.win_rate_pct, "%")} · avg {f(x.avg_pnl_pct, "%")} ·{" "}
        <span style={{ color: (x.profit_factor ?? 0) >= 1 ? "var(--bull)" : "var(--bear)" }}>PF {f(x.profit_factor)}</span></span>
    : "—");
  const Row = ({ label, children, sub }) => (
    <tr className="border-b border-grid/40">
      <td className={`px-3 py-1.5 ${sub ? "pl-6 text-muted-3" : "text-muted-2"}`}>{label}</td>
      <td className="px-3 py-1.5">{children}</td>
    </tr>
  );
  const rows = (d.skips || []).filter((r) => r.setup === setup);
  return (
    <div className="border border-grid p-4 space-y-2" data-testid="journal-setup-shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
          Skipped picks &amp; trade excursions (log only)
        </div>
        <select value={setup} onChange={(e) => setSetup(e.target.value)}
                className="bg-transparent border border-grid text-[10px] font-mono-data px-2 py-1">
          {["Candle Breakout", "Sniper", "VWAP Bounce"].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <div className="text-[10px] text-muted-3 font-mono-data">
        Each filter-dropped pick is replayed on real 1-min option prices with the setup's own target/stop from the price
        at the skip (stop assumed first if both hit in one minute). Sniper: only picks that failed exactly one filter.
        Nothing is traded. {s.pending_skips ? `${s.pending_skips} skip(s) waiting for the 15:47 replay.` : ""}
      </div>
      <table className="w-full text-[11px] font-mono-data">
        <tbody>
          <Row label="Real trades">{st(s.real)}</Row>
          <Row label="All skipped (replayed)">{st(s.skipped_all)}</Row>
          {Object.entries(s.skipped_by_reason || {}).map(([k, v]) => <Row key={k} label={`· ${k}`} sub>{st(v)}</Row>)}
          <Row label="Stop-outs that later hit target">
            {s.stops ? `${s.whipsaw_stops} of ${s.stops} (${f(s.whipsaw_rate_pct, "%")})` : "—"}
          </Row>
          <Row label="Avg best / worst in 30 min after entry">
            {f(s.avg_mfe_30m_pct, "%")} / {f(s.avg_mae_30m_pct, "%")} · winners' best {f(s.winners_avg_mfe_30m_pct, "%")}
          </Row>
        </tbody>
      </table>
      {rows.length > 0 && (
        <>
          <button className="text-[10px] underline text-muted-2 font-mono-data" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"} skipped picks
          </button>
          {open && (
            <table className="w-full text-[11px] font-mono-data">
              <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
                <tr className="border-b border-grid">
                  {["Day", "Time", "Symbol", "Side", "Reason", "Entry", "Outcome", "P&L", "Best 30m"].map((h) => (
                    <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-grid/40">
                    <td className="px-3 py-1.5">{r.day}</td>
                    <td className="px-3 py-1.5">{r.t}</td>
                    <td className="px-3 py-1.5 font-semibold">{r.symbol}</td>
                    <td className="px-3 py-1.5">{r.side}</td>
                    <td className="px-3 py-1.5 text-muted-2">{r.reason}</td>
                    <td className="px-3 py-1.5">{f(r.entry)}</td>
                    <td className="px-3 py-1.5">{r.outcome ? `${r.outcome}${r.exit_t ? ` ${r.exit_t}` : ""}` : "pending"}</td>
                    <td className="px-3 py-1.5" style={{ color: (r.pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
                      {r.pnl_pct == null ? "—" : `${r.pnl_pct >= 0 ? "+" : ""}${r.pnl_pct}%`}
                    </td>
                    <td className="px-3 py-1.5">{f(r.mfe_pct, "%")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

const JournalPage = () => {
  const [trades, setTrades] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [expiryPhase, setExpiryPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  // 2026-09-23 (chat: "Journal page is sometimes loads slowly... default
  // selection to Today and make it faster") — was 30, meaning every page
  // load scanned a full month of trades across every setup by default,
  // growing daily as more trades accumulate (most of these collections
  // keep all history — since 2026-11-01 no paper/live trade TTL, see server.py).
  // "Today" is both the fastest default (smallest query window) and the
  // most commonly useful one; wider windows are still one click away.
  const [days, setDays] = useState(0);
  // 2026-08-31 — chat: "Remove or Hide 'Stayed paper' entries. Too many
  // entries, frontend looks clumsy" — the badge added earlier (to explain
  // why card count != rollup stats count) made the clutter worse on a day
  // with lots of downgraded signals (5 VWAP Bounce A + 1 OI Long Buildup
  // out of 13 cards). Hide these cards by default instead of always
  // rendering them; the toggle below still lets them be pulled up on
  // demand (e.g. reviewing why a setup keeps getting downgraded) without
  // permanently losing that ability.
  const [showStayedPaper, setShowStayedPaper] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [t, r, e] = await Promise.all([
          axios.get(`${API}/journal/trades?days=${days}`),
          axios.get(`${API}/journal/rollup?days=${days}`),
          axios.get(`${API}/journal/expiry-phase-rollup?days=${days}`),
        ]);
        setTrades(t.data?.trades || []);
        setRollup(r.data || null);
        setExpiryPhase(e.data || null);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, [days]);
  const saveNote = async (key, note) => {
    await axios.post(`${API}/journal/note`, { trade_key: key, note });
  };
  const stayedPaperCount = trades.filter(isTradeDowngraded).length;
  const visibleTrades = showStayedPaper ? trades : trades.filter((t) => !isTradeDowngraded(t));
  return (
    <section className="space-y-6" data-testid="journal-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Post-mortem
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2">
          <BookOpen size={20} /> Trade Journal
        </h2>
        <p className="text-xs text-muted-2 mt-1 max-w-2xl">
          Every closed paper &amp; live trade across all setups (VWAP A/B/C, VWAP+OI, Setup E, ORB, Gap-and-Go, OI Long Buildup, Candle Breakout, Sniper Setups).
          Auto-computed patterns highlight your best day-of-week, best entry hour, and best-performing setup.
          Add free-form notes to build your own playbook.
        </p>
      </div>
      <div className="flex items-center gap-2 font-mono-data text-[11px]">
        <span className="text-muted-3 uppercase tracking-wider">Window</span>
        {[
          { d: 0, label: "Today" },
          { d: 7, label: "7d" },
          { d: 14, label: "14d" },
          { d: 30, label: "30d" },
          { d: 60, label: "60d" },
          { d: 90, label: "90d" },
        ].map(({ d, label }) => (
          <button key={d} onClick={() => setDays(d)}
            data-testid={`journal-window-${d}`}
            className="px-2.5 py-1 border font-mono-data"
            style={{
              borderColor: days === d ? "var(--bull)" : "var(--border-grid)",
              color: days === d ? "var(--bull)" : "var(--text-muted-2)",
            }}>
            {label}
          </button>
        ))}
        <span className="ml-auto text-muted-3">
          {rollup?.total_trades ?? 0} trades ·{" "}
          <span style={{ color: (rollup?.total_pnl_pct ?? 0) >= 0 ? "var(--bull)" : "var(--bear)" }}>
            {(rollup?.total_pnl_pct ?? 0) >= 0 ? "+" : ""}{fmt(rollup?.total_pnl_pct, 2)}%
          </span> · {fmt(rollup?.win_rate_pct, 1)}% wins
        </span>
        {/* 2026-08-31 — chat: "Remove or Hide 'Stayed paper' entries. Too
            many entries, frontend looks clumsy" — cards for trades the
            strategy's own pre-order logic downgraded (R:R floor, red-candle
            filter, etc.) are hidden by default now; this toggle is the only
            way back to them, since they're still useful for reviewing why a
            setup keeps getting downgraded. */}
        {stayedPaperCount > 0 && (
          <button onClick={() => setShowStayedPaper((v) => !v)}
            data-testid="journal-toggle-stayed-paper"
            className="px-2 py-1 border font-mono-data text-[10px] uppercase"
            style={{ borderColor: "var(--border-grid)", color: "#FFB66E" }}>
            {showStayedPaper ? "Hide" : "Show"} {stayedPaperCount} stayed paper
          </button>
        )}
      </div>
      {rollup && <RollupStrip rollup={rollup} />}
      {rollup && <CautionsPanel cautions={rollup.cautions} />}
      {rollup && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <SetupStatsTable byFamily={rollup.by_family} />
          <HourBreakdownTable rollup={rollup} />
        </div>
      )}
      {rollup && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <LateEntryCard days={days} />
          <MoodBreakdownTable rollup={rollup} />
        </div>
      )}
      {rollup && <RegimeCard days={days} />}
      {rollup && <SetupShadowCard days={days} />}
      {rollup && <EntryOddsCard days={days} />}
      {rollup && <StockOptionCtxCard days={days} />}
      <StockLabCard />
      {expiryPhase && <ExpiryPhaseTable data={expiryPhase} />}
      {loading ? (
        <p className="text-sm text-muted-2 font-mono-data">Loading…</p>
      ) : visibleTrades.length === 0 ? (
        <div className="border border-grid p-6 text-center">
          <p className="text-sm text-muted-2">
            {trades.length === 0
              ? `No closed trades ${days === 0 ? "today" : `in the last ${days} days`}.`
              : "All trades in this window stayed paper — toggle above to view them."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visibleTrades.map((t) => (
            <TradeCard key={t.trade_key} trade={t} onSaveNote={saveNote} />
          ))}
        </div>
      )}
    </section>
  );
};

export default JournalPage;